/**
 * OnboardingGuideCard
 * -------------------
 * Phase 4.5 — Dashboard Guidance Anchor.
 *
 * A single, anchored guidance card rendered at the top of the Vendor
 * Dashboard (directly under the greeting, above "Your Business"). Gives
 * vendors an immediate, unambiguous next action based on the canonical
 * onboarding_phase from OnboardingContext.
 *
 * Design rules (see Phase 4.5 brief):
 *   - Renders ONLY when onboarding_phase !== 'live' AND phase is one of
 *     { not_started, role_granted, profile_draft, ready_to_publish,
 *       payouts_pending }. Fresh vendors (role_granted / not_started) see
 *     the same "Get your business ready" copy as profile_draft so they
 *     never land on an empty dashboard.
 *   - Exactly ONE guidance element — no duplicate signals.
 *   - Premium card styling (rounded, soft shadow, phase-tinted), NOT a banner.
 *   - Additive only: does not reintroduce removed onboarding signals.
 *   - Relies solely on useOnboarding(); no local recomputation.
 */

import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useOnboarding } from '../context/OnboardingContext';

const PHASE_COPY = {
  // Fresh vendor — no agreement accepted yet OR no vendor doc. The
  // dashboard CTA opens the participation-agreement modal, so the copy
  // must reflect that action (was previously mismatched: copy implied
  // profile setup while CTA opened the agreement dialog).
  not_started: {
    title: 'One last step to activate your profile',
    body: 'Accept the platform agreement to start receiving requests',
    cta: 'Review & Accept Agreement',
    tab: 'profile',
    tint: {
      card: 'bg-amber-50/60 border-amber-200/80',
      title: 'text-amber-950',
      body: 'text-amber-900/80',
      button: 'bg-amber-600 hover:bg-amber-700 text-white',
    },
  },
  role_granted: {
    title: 'One last step to activate your profile',
    body: 'Accept the platform agreement to start receiving requests',
    cta: 'Review & Accept Agreement',
    tab: 'profile',
    tint: {
      card: 'bg-amber-50/60 border-amber-200/80',
      title: 'text-amber-950',
      body: 'text-amber-900/80',
      button: 'bg-amber-600 hover:bg-amber-700 text-white',
    },
  },
  profile_draft: {
    title: 'Get your business ready',
    body: 'Complete your profile to start receiving requests',
    cta: 'Finish Setup',
    tab: 'profile',
    tint: {
      card: 'bg-amber-50/60 border-amber-200/80',
      title: 'text-amber-950',
      body: 'text-amber-900/80',
      button: 'bg-amber-600 hover:bg-amber-700 text-white',
    },
  },
  ready_to_publish: {
    title: 'You\u2019re ready to go live',
    body: 'Publish your profile to start receiving bookings',
    cta: 'Go Live',
    tab: 'profile',
    tint: {
      card: 'bg-emerald-50/60 border-emerald-200/80',
      title: 'text-emerald-950',
      body: 'text-emerald-900/80',
      button: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    },
  },
  payouts_pending: {
    title: 'Finish getting paid',
    body: 'Connect payouts to accept deposits and receive payments',
    cta: 'Set Up Payouts',
    tab: 'payments',
    tint: {
      card: 'bg-sky-50/60 border-sky-200/80',
      title: 'text-sky-950',
      body: 'text-sky-900/80',
      button: 'bg-sky-600 hover:bg-sky-700 text-white',
    },
  },
};

/**
 * @param {object}   props
 * @param {(target: {tab:string, type:'tab'|'agreement'}) => void} props.onCtaClick
 *   Callback used for CTA click. Receives an action descriptor:
 *     { tab: 'profile' | 'payments', type: 'tab' | 'agreement' }.
 *   Parent is responsible for the actual resolution — tab switch for
 *   `type: 'tab'`, or opening the participation-agreement dialog (and then
 *   continuing to the tab) for `type: 'agreement'`.
 */
const OnboardingGuideCard = ({ onCtaClick }) => {
  const { phase, ctaAction, hasMeaningfulProfile, requiredFieldsMissing } = useOnboarding();

  // Strict visibility gate — render only for phases with a known CTA action.
  // Hidden for: null, live, or any unmapped phase.
  let copy = phase ? PHASE_COPY[phase] : null;
  if (!copy || !ctaAction) return null;

  // Phase 4.6 — Profile-first override.
  // The backend's canonical phase resolver short-circuits to `role_granted`
  // (agreement gate) before evaluating profile readiness. That means a brand
  // new vendor with missing required fields (Contact Person, Profile Photo,
  // Phone, etc.) was previously shown agreement copy — misleading because the
  // agreement is NOT the next real blocker. We now consult the backend-
  // sourced `required_fields_missing` list (surfaced for ALL phases on
  // /vendor/onboarding-status) and downgrade the card to profile copy when
  // required fields are still missing. OnboardingContext applies the matching
  // ctaAction downgrade so the CTA routes to the profile tab (not the
  // agreement dialog).
  const requiredMissingCount = Array.isArray(requiredFieldsMissing) ? requiredFieldsMissing.length : 0;
  const profileFirstOverride =
    (phase === 'role_granted' || phase === 'not_started') && requiredMissingCount > 0;

  if (profileFirstOverride) {
    copy = {
      ...copy,
      title: 'Finish setting up your profile',
      body: 'Complete the required fields to activate your profile',
      cta: 'Complete Profile',
      tab: 'profile',
    };
  } else if (hasMeaningfulProfile && (phase === 'role_granted' || phase === 'not_started')) {
    // Phase 4.5 historical note: this swap was needed when role_granted /
    // not_started copy used profile-setup wording. The base PHASE_COPY now
    // already uses agreement-first copy, so the swap is a no-op. Kept as a
    // defensive layer in case PHASE_COPY ever drifts back to profile wording.
    copy = {
      ...copy,
      title: 'One last step to activate your profile',
      body: 'Accept the platform agreement to start receiving requests',
      cta: 'Review & Accept Agreement',
    };
  }

  const handleClick = () => {
    if (typeof onCtaClick === 'function') {
      onCtaClick({ tab: ctaAction.tab, type: ctaAction.type });
    }
  };

  return (
    <div
      data-testid="onboarding-guide-card"
      data-phase={phase}
      className={`rounded-2xl border shadow-sm p-4 sm:p-5 ${copy.tint.card}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <h3
            className={`text-base sm:text-lg font-semibold leading-tight ${copy.tint.title}`}
            data-testid="onboarding-guide-title"
          >
            {copy.title}
          </h3>
          <p
            className={`text-sm mt-1 ${copy.tint.body}`}
            data-testid="onboarding-guide-body"
          >
            {copy.body}
          </p>
        </div>

        <div className="sm:shrink-0">
          <button
            type="button"
            onClick={handleClick}
            data-testid="onboarding-guide-cta"
            className={`inline-flex items-center justify-center gap-1.5 w-full sm:w-auto px-4 py-2 rounded-full text-sm font-semibold transition-colors active:scale-[0.98] ${copy.tint.button}`}
          >
            {copy.cta}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingGuideCard;
